import { useEffect } from 'react'

import { usePermissionsContext } from 'context/PermissionsContext'
import {
  getFirstAllowedTab,
  getRequiredPermission,
  hasAccessToRoute,
  ROUTE_PERMISSIONS
} from 'lib/permissions/route-permissions'
import { useRouter } from 'next/router'

interface UseRouteProtectionOptions {
  /**
   * Rota para redirecionar quando nao tem permissao
   * @default '/visao-geral' ou primeira rota permitida
   */
  fallbackRoute?: string

  /**
   * Se deve redirecionar automaticamente
   * @default true
   */
  autoRedirect?: boolean

  /**
   * Se deve habilitar a protecao (util para desabilitar durante carregamento)
   * @default true
   */
  enabled?: boolean
}

interface UseRouteProtectionReturn {
  /**
   * Se o usuario tem permissao para a rota/tab atual
   */
  hasAccess: boolean

  /**
   * Se esta verificando permissoes (loading)
   */
  isChecking: boolean

  /**
   * Permissao necessaria para a rota atual
   */
  requiredPermission: string | null
}

/**
 * Hook para proteger rotas baseado em permissoes
 *
 * Verifica se o usuario tem permissao para acessar a rota/tab atual
 * e redireciona automaticamente se nao tiver.
 *
 * @example
 * // No Layout.tsx ou em uma pagina
 * const { hasAccess, isChecking } = useRouteProtection()
 *
 * if (isChecking) return <Loader />
 * // O hook ja redireciona automaticamente se nao tiver acesso
 */
export function useRouteProtection(
  options: UseRouteProtectionOptions = {}
): UseRouteProtectionReturn {
  const { fallbackRoute, autoRedirect = true, enabled = true } = options

  const router = useRouter()
  const { pathname, query, isReady } = router
  const { hasPermission, hasAnyPermission, permissions } =
    usePermissionsContext()

  const tab = query.tab as string | undefined
  const requiredPermission = getRequiredPermission(pathname, tab)

  // Verifica se o usuario tem acesso
  const hasAccess = requiredPermission
    ? hasPermission(requiredPermission)
    : true

  // Verifica se ainda esta carregando
  // Consideramos "checking" quando:
  // 1. O router ainda nao esta pronto
  // 2. Temos permissoes mas ainda nao carregaram (array vazio pode ser legado)
  const isChecking = !isReady

  useEffect(() => {
    // Nao faz nada se desabilitado ou ainda verificando
    if (!enabled || !isReady) return

    // Se nao tem permissoes no contexto, pode ser sistema legado
    // Nesse caso, nao bloqueia (fallback para sistema de roles)
    if (permissions.length === 0) return

    // Se tem acesso, nao precisa redirecionar
    if (hasAccess) return

    // Se nao deve redirecionar automaticamente, apenas retorna
    if (!autoRedirect) return

    // Tenta encontrar uma tab permitida na mesma rota
    if (ROUTE_PERMISSIONS[pathname]?.tabs) {
      const allowedTab = getFirstAllowedTab(pathname, hasPermission)
      if (allowedTab) {
        // Redireciona para a tab permitida na mesma rota
        router.replace({
          pathname,
          query: { ...query, tab: allowedTab }
        })
        return
      }
    }

    // Nao tem tab permitida, tenta encontrar uma rota permitida
    const target = fallbackRoute
      ? { pathname: fallbackRoute }
      : findFirstAllowedRouteWithTab(hasPermission, hasAnyPermission)

    if (target && target.pathname !== pathname) {
      router.replace(target)
    }
  }, [
    enabled,
    isReady,
    hasAccess,
    autoRedirect,
    pathname,
    tab,
    permissions,
    hasPermission,
    hasAnyPermission,
    fallbackRoute,
    router,
    query
  ])

  return {
    hasAccess,
    isChecking,
    requiredPermission
  }
}

interface RouteWithTab {
  pathname: string
  query?: { tab: string }
}

/**
 * Encontra a primeira rota (com tab, se necessario) que o usuario tem acesso
 */
function findFirstAllowedRouteWithTab(
  hasPermission: (permission: string) => boolean,
  hasAnyPermission: (permissions: string[]) => boolean
): RouteWithTab | null {
  // Ordem de preferencia das rotas
  const routeOrder = [
    '/visao-geral',
    '/comercial',
    '/clientes',
    '/cobrancas',
    '/operacao'
  ]

  for (const route of routeOrder) {
    // Verifica se tem acesso a pelo menos algo nessa rota
    if (!hasAccessToRoute(route, hasAnyPermission)) {
      continue
    }

    const config = ROUTE_PERMISSIONS[route]

    // Se a rota tem permissao default (ex: /visao-geral com dashboard:view)
    if (config?.default && hasPermission(config.default)) {
      return { pathname: route }
    }

    // Se a rota tem tabs, encontra a primeira tab permitida
    if (config?.tabs) {
      const allowedTab = getFirstAllowedTab(route, hasPermission)
      if (allowedTab) {
        return { pathname: route, query: { tab: allowedTab } }
      }
    }

    // Rota sem configuracao de permissao (publica)
    if (!config) {
      return { pathname: route }
    }
  }

  // Nenhuma rota permitida encontrada
  return null
}

export default useRouteProtection
